import React from "react";
import "./Project.css";
import Wrapper from "../components/basic/wrapper";
import SafeArea from "../components/basic/safe-area";
import PhotoLiner from "../components/basic/photo-liner/PhotoLiner";
import Testimonios from "../components/sections/obra/Testimonios";
import director from "../assets/images/webp/work_director_mobile.webp";
import diana from "../assets/images/webp/members/1-DIANA.webp";
import jaime from "../assets/images/webp/members/4-JAIME.webp";
import xime from "../assets/images/webp/members/2-XIME.webp";
import lucas from "../assets/images/webp/members/6-LUCAS.webp";
import octavio from "../assets/images/webp/members/4-OCTAVIO.webp";
import cris from "../assets/images/webp/members/3-CRIS.webp";
import alvaro from "../assets/images/webp/members/7-ÁLVARO.webp";
import manuel from "../assets/images/webp/members/8-MANUEL.webp";
// direction team
import luis from "../assets/images/webp/direction_team/project_direction_team_Luis.webp";
import jonathan from "../assets/images/webp/direction_team/project_direction_team_Jonathan.webp";
import claudia from "../assets/images/webp/direction_team/project_direction_team_Claudia.webp";
import luis_mobile from "../assets/images/webp/direction_team/project_direction_team_mobile_Luis.webp";
import jonathan_mobile from "../assets/images/webp/direction_team/project_direction_team_mobile_Jonathan.webp";
import claudia_mobile from "../assets/images/webp/direction_team/project_direction_team_mobile_Claudia.webp";
import Prensa from "../components/sections/obra/Prensa";
import equipo_de_direccion from "../assets/images/webp/project_direction_team.webp";
import fichaArtistic from "../assets/images/webp/ficha_artistic.webp";
import fichaArtisticShadowed from "../assets/images/webp/ficha_artistic_shadowed.webp";
import Networks from "../components/basic/social-networks-row/Networks";
import PhotoLinerV2 from "../components/basic/photo-liner-v2/PhotoLinerV2";
// other pics
import cf from "../assets/images/webp/work_director.webp";
import cfShadowed from "../assets/images/webp/work_director_shadowed.webp";

import { useI18n } from "../i18n/i18n";

const Project: React.FC = () => {
    const sectionColor = "#ffffff";
    const sectionTextColor = "#FFFFFF";

    const { t } = useI18n();

    const girasDates = React.useMemo(
        () => t("project.tour.dates").split("\n").filter(Boolean),
        [t]
    );
    const tourEmail = t("project.tour.contactEmail");
    const tourTechUrl = t("project.tour.techUrl");

    const direction_team_members = [
        {
            name: "Luis Alberto León",
            photo: luis,
            photoMobile: luis_mobile,
            description: t("project.directionTeam.luis.description"),
        },
        {
            name: "Jonathan Oliveros",
            photo: jonathan,
            photoMobile: jonathan_mobile,
            description: t("project.directionTeam.jonathan.description"),
        },
        {
            name: "Claudia Tangoa",
            photo: claudia,
            photoMobile: claudia_mobile,
            description: t("project.directionTeam.claudia.description"),
        },
    ];

    const DEFAULT_BG = equipo_de_direccion;

    const [hoveredMember, setHoveredMember] = React.useState<(typeof direction_team_members)[0] | null>(null);
    const [currentIndexMobile, setCurrentIndexMobile] = React.useState(0);

    const trackRef = React.useRef<HTMLDivElement | null>(null);
    const slideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const [containerHeight, setContainerHeight] = React.useState<number | undefined>(
        slideRefs.current[currentIndexMobile]?.offsetHeight
    );

    const touchStartXRef = React.useRef<number | null>(null);
    const touchStartYRef = React.useRef<number | null>(null);
    const containerWidthRef = React.useRef<number>(0);
    const [dragX, setDragX] = React.useState(0);
    const [isDragging, setIsDragging] = React.useState(false);
    const [isFichaOpen, setIsFichaOpen] = React.useState(false);
    const [isFichaCFOpen, setIsFichaCFOpen] = React.useState(false);

    const toggleFicha = () => setIsFichaOpen((prev) => !prev);
    const toggleFichaCF = () => setIsFichaCFOpen((prev) => !prev);

    // preload both backgrounds once
    const usePreloadImages = (urls: string[]) => {
        React.useEffect(() => {
            urls.forEach((url) => {
                if (!url) return;
                const img = new Image();
                img.src = url;
            });
        }, [urls]);
    };
    usePreloadImages([fichaArtistic, fichaArtisticShadowed]);

    // lock body scroll when ficha is open
    React.useEffect(() => {
        if (isFichaOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [isFichaOpen]);

    const SWIPE_THRESHOLD_RATIO = 0.2; // 20% of width

    const handleNextMobile = () => {
        setCurrentIndexMobile((prev) => (prev + 1) % direction_team_members.length);
        setDragX(0);
    };

    const handlePrevMobile = () => {
        setCurrentIndexMobile(
            (prev) => (prev - 1 + direction_team_members.length) % direction_team_members.length
        );
        setDragX(0);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        touchStartXRef.current = touch.clientX;
        touchStartYRef.current = touch.clientY;
        setIsDragging(true);

        if (trackRef.current) {
            containerWidthRef.current = trackRef.current.clientWidth;
        } else {
            containerWidthRef.current = window.innerWidth;
        }
        setDragX(0);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (touchStartXRef.current == null || touchStartYRef.current == null) return;

        const touch = e.touches[0];
        const diffX = touch.clientX - touchStartXRef.current;
        const diffY = touch.clientY - touchStartYRef.current;

        if (Math.abs(diffY) > Math.abs(diffX)) return;

        setDragX(diffX);
    };

    const handleTouchEnd = () => {
        if (!containerWidthRef.current) {
            setDragX(0);
            setIsDragging(false);
            return;
        }

        const threshold = containerWidthRef.current * SWIPE_THRESHOLD_RATIO;

        if (Math.abs(dragX) > threshold) {
            if (dragX < 0) handleNextMobile();
            else handlePrevMobile();
        } else {
            setDragX(0);
        }

        setIsDragging(false);
        touchStartXRef.current = null;
        touchStartYRef.current = null;
    };

    // track translate
    const baseTranslatePercent = -currentIndexMobile * 100;
    const dragPercent =
        containerWidthRef.current > 0 ? (dragX / containerWidthRef.current) * 100 : 0;
    const trackTranslate = baseTranslatePercent + dragPercent;

    React.useEffect(() => {
        const el = slideRefs.current[currentIndexMobile];
        if (el) setContainerHeight(el.offsetHeight);
    }, [currentIndexMobile, direction_team_members.length]);

    React.useEffect(() => {
        const handleResize = () => {
            const el = slideRefs.current[currentIndexMobile];
            if (el) setContainerHeight(el.offsetHeight);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [currentIndexMobile]);

    const useImageCache = (urls: string[]) => {
        const [loaded, setLoaded] = React.useState<Record<string, boolean>>({});

        React.useEffect(() => {
            urls.forEach((url) => {
                if (!url || loaded[url]) return;

                const img = new Image();
                img.onload = () => {
                    setLoaded((prev) => ({ ...prev, [url]: true }));
                };
                img.src = url;
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [urls.join("|")]);

        return loaded;
    };

    const [bgUrl, setBgUrl] = React.useState<string>(DEFAULT_BG);

    const loaded = useImageCache([DEFAULT_BG, ...direction_team_members.map((m) => m.photo)]);

    const handleMouseEnter = (member: (typeof direction_team_members)[number]) => {
        setHoveredMember(member);

        const url = member.photo;

        if (loaded[url]) {
            setBgUrl(url);
        } else {
            const img = new Image();
            img.onload = () => setBgUrl(url);
            img.src = url;
        }
    };

    const handleMouseLeave = () => {
        setHoveredMember(null);
        setBgUrl(DEFAULT_BG);
    };

    usePreloadImages([DEFAULT_BG, ...direction_team_members.map((m) => m.photo)]);

    const photos_obras = [
        { id: 1, name: t("project.cast.diana.name"), url: diana },
        { id: 2, name: t("project.cast.jaime.name"), url: jaime },
        { id: 3, name: t("project.cast.ximena.name"), url: xime },
        { id: 4, name: t("project.cast.lucas.name"), url: lucas },
        { id: 5, name: t("project.cast.octavio.name"), url: octavio },
        { id: 6, name: t("project.cast.cristina.name"), url: cris },
        { id: 7, name: t("project.cast.alvaro.name"), url: alvaro },
        { id: 8, name: t("project.cast.manuel.name"), url: manuel },
    ];

    const synopsisLines = React.useMemo(
        () => t("project.synopsis").split("\n").filter(Boolean),
        [t]
    );

    return (
        <>
            <Wrapper color={sectionColor} footerColor={sectionTextColor} logoColor={sectionTextColor}>
                <>
                    <>
                        <div style={{ backgroundColor: sectionColor }}>
                            <div className="about-us-photo">
                                <div className="about-us-redes-container w100">
                                    <div className="fs64 fw700 about-us-title">
                                        <SafeArea>
                                            <div>{t("project.title")}</div>
                                        </SafeArea>
                                    </div>
                                    <div className="about-us-photo-redes w100">
                                        <SafeArea>
                                            <Networks extended scrollTargetId="project-scroll-target" />
                                        </SafeArea>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="obra-photos-content" id="project-scroll-target">
                            <SafeArea>
                                <div className="project-photo-grid gap8 w100">
                                    {photos_obras.map((photo) => (
                                        <div className="project-gallery-item" key={photo.id}>
                                            <img
                                                src={photo.url}
                                                alt={t("project.gallery.imageAlt")}
                                                className="project-gallery-image mb8"
                                            />
                                            <div className="project-gallery-caption">
                                                <span className="project-gallery-caption__name">{photo.name}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SafeArea>
                        </div>

                        <div className="project-sipnosis-wrapper">
                            <PhotoLiner title={t("project.synopsisTitle")} color="#000000" showLine />
                            <SafeArea>
                                <div className="project-sipnosis">
                                    {synopsisLines.map((line, idx) => (
                                        <span key={idx}>
                                            {line}
                                            {idx < synopsisLines.length - 1 ? <br /> : null}
                                        </span>
                                    ))}
                                </div>
                            </SafeArea>
                        </div>

                        <div
                            className={`project-director-desktop ${isFichaCFOpen ? "is-open" : ""}`}
                            style={{ backgroundImage: `url(${isFichaCFOpen ? cfShadowed : cf})` }}
                        >
                            <PhotoLiner title={t("project.director.sectionLabel")} color="#ffffff" />
                            <SafeArea>
                                <div className="d-flex jcsb aic w100">
                                    <div className="project-director-p1">
                                        <PhotoLinerV2
                                            title={t("project.director.name")}
                                            color="#ffffff"
                                            customClass="fs32 fw700"
                                            safe={false}
                                            onHoverChange={toggleFichaCF}
                                            hovered={isFichaCFOpen}
                                        />
                                    </div>
                                    <div className={`${isFichaCFOpen ? "visible" : "hidden"} project-director-p2`}>
                                        <div className="fs16 ln26" style={{ maxHeight: "750px", overflowY: "scroll" }}>
                                            <div className="ln26 fs16 mb36">{t("project.director.text.p1")}</div>
                                            <div className="ln26 fs16 mb36">{t("project.director.text.p2")}</div>
                                            <div className="ln26 fs16 mb36">{t("project.director.text.p3")}</div>
                                        </div>
                                    </div>
                                </div>
                            </SafeArea>
                        </div>

                        <div className="project-director-mobile">
                            <PhotoLiner title={t("project.director.sectionLabel")} color="#ffffff" />
                            <SafeArea>
                                <>
                                    <PhotoLinerV2
                                        title={t("project.director.name")}
                                        color="#ffffff"
                                        customClass="fs32 fw700"
                                        hovered
                                        safe={false}
                                    />
                                    <div className="fs16 ln26 mt32">
                                        <div className="ln26 fs16 mb36">{t("project.director.text.p1")}</div>
                                        <div className="ln26 fs16 mb36">{t("project.director.text.p2")}</div>
                                        <div className="ln26 fs16 mb36">{t("project.director.text.p3")}</div>
                                    </div>
                                </>
                            </SafeArea>
                            <img className="objcov" src={director} alt="" />
                        </div>

                        <div className="project-direction-team-desktop">
                            {/* Background layer that fades in */}
                            {direction_team_members.map((m) => (
                                <div
                                    key={m.name}
                                    className={"project-direction-bg is-visible"}
                                    style={{ backgroundImage: `url(${hoveredMember ? hoveredMember.photo : bgUrl})` }}
                                />
                            ))}

                            <PhotoLiner title={t("project.directionTeam.title")} color="#ffffff" />

                            <SafeArea>
                                <div className="d-flex jcsb aic project-direction-team-desktop-inner">
                                    <div className="project-direction-team-desktop-p1">
                                        {direction_team_members.map((member) => {
                                            const isActive = hoveredMember === member;
                                            const isDim = hoveredMember !== null && !isActive;

                                            return (
                                                <div
                                                    key={member.name}
                                                    className="pb20 d-flex aic project-direction-team-member"
                                                    onMouseEnter={() => handleMouseEnter(member)}
                                                    onMouseLeave={handleMouseLeave}
                                                >
                                                    <div className="project-direction-team-bullet-point" />
                                                    <div
                                                        className={`fs32 fw900 project-direction-team-bullet-text ${isDim ? "is-dim" : ""}`}
                                                        style={{ paddingLeft: "16px" }}
                                                    >
                                                        {member.name}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="project-direction-team-desktop-p2">
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: hoveredMember?.description ?? "",
                                            }}
                                        />
                                    </div>
                                </div>
                            </SafeArea>
                        </div>

                        <div className="project-direction-team-mobile">
                            <PhotoLiner title={t("project.directionTeam.title")} color="#ffffff" />

                            <div
                                className="project-direction-team-mobile-viewport"
                                style={{ height: containerHeight ?? "auto" }}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                <div
                                    ref={trackRef}
                                    className="project-direction-team-mobile-track"
                                    style={{
                                        transform: `translateX(${trackTranslate}%)`,
                                        transition: isDragging ? "none" : "transform 0.3s ease-out",
                                    }}
                                >
                                    {direction_team_members.map((member, index) => (
                                        <div
                                            key={member.name}
                                            className="project-direction-team-mobile-slide"
                                            ref={(el) => (slideRefs.current[index] = el)}
                                        >
                                            <div>
                                                <div style={{ position: "relative" }}>
                                                    <SafeArea>
                                                        <>
                                                            <div className="fs32 fw900" style={{ marginBottom: "46px" }}>
                                                                {member.name}
                                                            </div>
                                                            <div
                                                                style={{ position: "absolute", right: "4px", top: "0" }}
                                                                onClick={handleNextMobile}
                                                            >
                                                                <svg
                                                                    width="16"
                                                                    height="29"
                                                                    viewBox="0 0 16 29"
                                                                    fill="none"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        d="M0.353561 0.35347L14.4957 14.4956L0.353561 28.6377"
                                                                        stroke="white"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </>
                                                    </SafeArea>
                                                </div>

                                                <SafeArea>
                                                    <div
                                                        className="project-direction-team-mobile-content"
                                                        dangerouslySetInnerHTML={{
                                                            __html: member.description ?? "",
                                                        }}
                                                    />
                                                </SafeArea>
                                            </div>

                                            <br />

                                            <img
                                                className="objcov w100"
                                                src={member.photoMobile ?? member.photo}
                                                alt={member.name}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`project-ficha-artistica-desktop ${isFichaOpen ? "is-open" : ""}`}
                            style={{ backgroundImage: `url(${isFichaOpen ? fichaArtisticShadowed : fichaArtistic})` }}
                        >
                            <PhotoLinerV2
                                title={t("project.playCredits.title")}
                                color="#ffffff"
                                customClass="fs32 fw700"
                                absolute
                                onHoverChange={toggleFicha}
                                hovered={isFichaOpen}
                            />

                            <div className={"project-ficha-artistica-overlay" + (isFichaOpen ? " is-visible" : "")}>
                                <div className="project-ficha-artistica-overlay-inner d-flex flex-col ln26 fw900 tac">
                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.company.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.company.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.playwritingAndDirection.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.playwritingAndDirection.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.associateDirection.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.associateDirection.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.cast.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.cast.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.vocalCoaching.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.vocalCoaching.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.choreography.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.choreography.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.visuals.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.visuals.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.lightingDesign.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.lightingDesign.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.productionStage.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.productionStage.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.touringProduction.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.touringProduction.value")}</span>
                                    </div>

                                    <div className="credit-row d-inline">
                                        <span className="credit-row-left d-inline">{t("project.playCredits.tourDirector.label")} </span>
                                        <span className="credit-row-right d-inline">{t("project.playCredits.tourDirector.value")}</span>
                                    </div>

                                    <div className="d-flex aib gap8 jcc">
                                        <span className="fw700 fs12">{t("project.playCredits.runningTime")}</span>
                                    </div>

                                    <div className="d-flex aib gap8 jcc">
                                        <span className="fw700 fs12">{t("project.playCredits.premiere")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="project-ficha-artistica-mobile">
                            <SafeArea>
                                <>
                                    <PhotoLinerV2
                                        title={t("project.playCredits.title")}
                                        color="#ffffff"
                                        customClass="fs32 fw700"
                                        hovered
                                        safe={false}
                                    />

                                    <div className="up tac d-flex flex-col ln26 fw900 mt50">
                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.company.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.company.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.playwritingAndDirection.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.playwritingAndDirection.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.associateDirection.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.associateDirection.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.cast.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.cast.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.vocalCoaching.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.vocalCoaching.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.choreography.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.choreography.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.visuals.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.visuals.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.lightingDesign.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.lightingDesign.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.productionStage.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.productionStage.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.touringProduction.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.touringProduction.value")}</span>
                                        </span>

                                        <span className="credit-row d-inline">
                                            <span className="credit-row-left d-inline">{t("project.playCredits.tourDirector.label")}&nbsp;</span>
                                            <span className="credit-row-right d-inline">{t("project.playCredits.tourDirector.value")}</span>
                                        </span>

                                        <div className="d-flex aib gap8 jcc">
                                            <span className="fw700 fs12">{t("project.playCredits.runningTime")}</span>
                                        </div>

                                        <div className="d-flex aib gap8 jcc">
                                            <span className="fw700 fs12">{t("project.playCredits.premiere")}</span>
                                        </div>
                                    </div>
                                </>
                            </SafeArea>

                            <img
                                className="objcov"
                                src="https://images-extras.s3.us-east-1.amazonaws.com/project_ficha_artistica_mobile.png"
                                alt=""
                            />
                        </div>

                        <Prensa />
                        {/* <Testimonios /> */}

                        <div className="project-giras-desktop">
                            <PhotoLiner title={t("project.tour.title")} color="#ffffff" showLine />
                            <SafeArea>
                                <div className="d-flex jcsb ais w100">
                                    <div className="project-giras-p1">
                                        <div className="fs16 ln26">
                                            <div>
                                                <span>{t("project.tour.contactLabel")} </span>
                                                <b>{t("project.tour.contactName")},</b>
                                            </div>
                                            <div className="textGray fw900">{t("project.tour.contactRole")}</div>
                                            <div className="textGray">
                                                <a href={`mailto:${tourEmail}`} style={{ textDecoration: "underline" }}>
                                                    {tourEmail}
                                                </a>
                                            </div>
                                        </div>

                                        <div className="fs16 ln26 mt32">
                                            <div>{t("project.tour.techLabel")}</div>
                                            <a
                                                href={tourTechUrl}
                                                target="_blank"
                                                className="textGray"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: "underline", wordBreak: "break-all" }}
                                            >
                                                {tourTechUrl}
                                            </a>
                                        </div>
                                    </div>

                                    <div className="project-giras-p2 fs16 ln26">
                                        {girasDates.map((line, index) => (
                                            <div key={index}>{line}</div>
                                        ))}
                                    </div>
                                </div>
                            </SafeArea>
                        </div>

                        <div className="project-giras-mobile">
                            <PhotoLiner title={t("project.tour.title")} color="#ffffff" showLine />
                            <SafeArea>
                                <div>
                                    <div>
                                        <div className="fs16 ln26">
                                            <div>
                                                <span>{t("project.tour.contactLabel")} </span>
                                                <b>{t("project.tour.contactName")},</b>
                                            </div>
                                            <div className="textGray fw900">{t("project.tour.contactRole")}</div>
                                            <div className="textGray">
                                                <a href={`mailto:${tourEmail}`} style={{ textDecoration: "underline" }}>
                                                    {tourEmail}
                                                </a>
                                            </div>
                                        </div>

                                        <div className="fs16 ln26 mt32">
                                            <div>{t("project.tour.techLabel")}</div>
                                            <a
                                                href={tourTechUrl}
                                                target="_blank"
                                                className="textGray"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: "underline", wordBreak: "break-all" }}
                                            >
                                                {tourTechUrl}
                                            </a>
                                        </div>
                                    </div>

                                    <div className="fs16 ln26 mt70">
                                        {girasDates.map((line, index) => (
                                            <div key={index}>{line}</div>
                                        ))}
                                    </div>
                                </div>
                            </SafeArea>

                            <img
                                className="objcov"
                                src="https://images-extras.s3.us-east-1.amazonaws.com/project_giras_mobile.png"
                                alt=""
                            />
                        </div>
                    </>
                </>
            </Wrapper>
        </>
    );
};

export default Project;
